import head from 'lodash/head';
import last from 'lodash/last';

import {DateKeyedFunnelSegments, FunnelSegment, SelectMode} from 'common/report/models/funnels/types';
import {MAX_SEGMENTS, OVERALL_PROP, SEGMENT_SEPARATOR} from 'common/report/models/funnels/constants';
import {Placement} from 'common/components/tooltip/types';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {getSelectedSegments} from 'common/report/util/funnels/utils';
import {AbsoluteDate} from 'common/widgets/date-range-picker/models/types';
import {formatDate, parseDate, transformDateByRelativeDate} from 'common/util/date';
import {isGroupedEntry} from 'common/widgets/query-entry-section/types';
import {BarStyle, SelectedSegmentStep} from 'common/report/models/funnels/bar-chart/types';
import {getFunnelStep} from 'common/report/models/funnels/bar-chart/utils';

import {FunnelsQuery} from './funnels-query';

/**
 * helper function to update TopPathRenameMap
 * eventually we should move FunnelsQuery into common and avoid duplicated functions
 */
function _addToTopPathsRenameMap(renameMap: {[key: string]: string}, segment: FunnelSegment, labels: string[]) {
  if (last(segment.props).prop !== `undefined` && ![`$other`, OVERALL_PROP].includes(head(segment.props).prop)) {
    renameMap[segment.prop] = segment.props
      .slice(0, -1)
      .map((item) => item.propName)
      .concat(
        last(segment.props)
          .prop.split(`,`)
          .map((stepIdx) => labels[stepIdx] || stepIdx)
          .join(` ➞ `),
      )
      .join(SEGMENT_SEPARATOR);
  }
}

/**
 * Computes mapping from segment names in the data to user friendly names.
 * Currently, it returns the mapping for converting any order segments
 * from step indices to step names.
 */
export function getTopPathsRenameMap(
  funnelsQuery: FunnelsQuery,
  funnelSegments: DateKeyedFunnelSegments[],
): {[key: string]: string} {
  const renameMap: {[key: string]: string} = {};
  if (funnelsQuery.isAnyOrderFunnel() && funnelsQuery.isSegmentedByTopPaths()) {
    const labels = funnelsQuery.getStepLabels(true);
    for (const currentDate of funnelSegments || []) {
      for (const segment of currentDate[`segments`]) {
        _addToTopPathsRenameMap(renameMap, segment, labels);
      }
    }
  }
  return renameMap;
}

/** Apply top-paths rename map to all segments */
export function getTopPathRenamedSegments(
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
): FunnelSegment[] {
  const segmentRenameMap = getTopPathsRenameMap(funnelsQuery, segmentData);
  const segments = segmentData?.[0]?.segments;
  return Object.values(segments).map((item) => {
    return item.prop in segmentRenameMap ? {...item, propName: segmentRenameMap[item.prop]} : item;
  });
}

/** helper function to get conversion count on last step */
function _getNumericFunnelConversion(steps) {
  const lastStep = steps[steps.length - 1];
  return lastStep.count;
}

/**
 * Get all data segments, transformed by the top-paths rename and ordered by conversions
 */
export function getRenamedOrderedSegmentSteps(funnelsQuery: FunnelsQuery, segmentData: DateKeyedFunnelSegments[]) {
  return getTopPathRenamedSegments(funnelsQuery, segmentData).sort((a, b) => {
    if (a.prop === OVERALL_PROP) {
      return -1;
    } else if (b.prop === OVERALL_PROP) {
      return 1;
    } else {
      return _getNumericFunnelConversion(b.steps) - _getNumericFunnelConversion(a.steps);
    }
  });
}

export function getLegendSections(
  selected: ColorChoice[],
  selectMode: SelectMode,
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
): Section[] {
  const segments = getRenamedOrderedSegmentSteps(funnelsQuery, segmentData);
  if (segments.length === 1) {
    return null;
  }

  const selectedSegments = getSelectedSegments(
    selected,
    selectMode,
    getRenamedOrderedSegmentSteps(funnelsQuery, segmentData),
  );
  const isAtMaxSelections = selectedSegments.length >= MAX_SEGMENTS;
  const keyToColorMap = new Map(
    selectedSegments.map<[string, string]>((s) => [s.key, s.color]),
  );
  const items: SectionItem[] = segments.map((segment) => {
    const isSelected = keyToColorMap.has(segment.prop);
    const color = keyToColorMap.get(segment.prop);
    const isDisabled = !isSelected && isAtMaxSelections;
    return {
      color,
      label: segment.propName,
      value: segment.prop,
      isDisabled,
      isSelected,
      wrapLabel: true,
      tooltip: isDisabled ? {text: `Cannot select more than ${MAX_SEGMENTS} items`, placement: Placement.Left} : null,
    };
  });
  return [{items}];
}

function _formatTime(from: AbsoluteDate | Date, to: AbsoluteDate | Date): string {
  const customFormatting = {day: `MMM D`};
  if (!(from instanceof Date)) {
    from = parseDate(from);
  }

  if (!(to instanceof Date)) {
    to = parseDate(to);
  }
  return `${formatDate(from, {unit: `day`, customFormatting})} - ${formatDate(to)}`;
}

export function getCalendarDateLabels(
  funnelsQuery: FunnelsQuery,
  comparisonSegmentData: DateKeyedFunnelSegments[],
  projectAsOfDate?: string,
): string[] {
  const {dateRange} = funnelsQuery;
  const comparisonFrom = comparisonSegmentData?.[0].date;
  const comparisonTo = transformDateByRelativeDate(comparisonFrom, dateRange.getRelativeDate());
  const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate);
  return [_formatTime(from, to), _formatTime(comparisonFrom, comparisonTo)];
}

export function isQueryGroupedSteps(funnelsQuery: FunnelsQuery) {
  return funnelsQuery.events.some((event) => isGroupedEntry(event));
}

export function createBucketLineChartFunnelData({
  segmentData,
  selected,
  stepIndex,
  hasComparison = false,
  isComparisonData = false,
}: {
  segmentData: FunnelSegment[];
  selected: ColorChoice[];
  stepIndex: number | null;
  hasComparison: boolean;
  isComparisonData: boolean;
}): Map<string, SelectedSegmentStep> {
  let style: BarStyle = null;
  const selectedSegments = new Map<string, string>(selected.map((s) => [s.key, s.color]));
  const data = new Map<string, SelectedSegmentStep>();

  // if there is comparison data, we need to set style
  if (hasComparison) {
    style = isComparisonData ? BarStyle.Stripe : BarStyle.Solid;
  }

  segmentData
    .filter((segment) => selectedSegments.has(segment.prop))
    .forEach((segment) => {
      data.set(segment.prop, {
        segmentKey: segment.prop,
        segmentName: segment.propName,
        step: getFunnelStep(segment.steps, stepIndex),
        color: selectedSegments.get(segment.prop),
        ...(style ? {style} : null),
      });
    });

  return data;
}
